import { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { DatePicker } from '@/components/ui/DatePicker';
import { cn } from '@/lib/utils';
import { centsToInputDollars, inputDollarsToCents } from '@/lib/formatters';
import { WEEKDAY_LABELS } from '@/features/manufacturing/planning/dates';
import {
  useAddPlanningHoliday,
  useDeletePlanningHoliday,
  useSavePlanningSettings,
  type PlanningHoliday,
} from '@/features/manufacturing/planning/hooks';
import type { PlanningSettings } from '@/features/manufacturing/planning/types';
import { shortDate } from './shared';

interface PlanningSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  settings: PlanningSettings;
  holidays: PlanningHoliday[];
  canEdit: boolean;
}

interface NumberFieldProps {
  id: string;
  label: string;
  help: string;
  value: string;
  onChange: (value: string) => void;
  suffix?: string;
  disabled?: boolean;
}

function NumberField({ id, label, help, value, onChange, suffix = 'days', disabled }: NumberFieldProps) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex items-center gap-2">
        <Input
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          inputMode="numeric"
          className="w-28 tabular-nums"
          disabled={disabled}
        />
        <span className="text-xs text-muted-foreground">{suffix}</span>
      </div>
      <p className="text-xs text-muted-foreground">{help}</p>
    </div>
  );
}

/**
 * Planning parameters + the holiday list. Working days and holidays
 * drive the calendar every target is spread over; the buffers sit on
 * top of lead times when the planner picks order dates.
 */
export function PlanningSettingsDialog({ open, onOpenChange, settings, holidays, canEdit }: PlanningSettingsDialogProps) {
  const save = useSavePlanningSettings();
  const addHoliday = useAddPlanningHoliday();
  const deleteHoliday = useDeletePlanningHoliday();

  const [workingDays, setWorkingDays] = useState<number[]>(settings.workingDays);
  const [horizon, setHorizon] = useState(String(settings.horizonDays));
  const [safetyBuffer, setSafetyBuffer] = useState(String(settings.safetyBufferDays));
  const [receivingBuffer, setReceivingBuffer] = useState(String(settings.receivingBufferDays));
  const [coverage, setCoverage] = useState(String(settings.orderCoverageDays));
  const [safetyStock, setSafetyStock] = useState(String(settings.safetyStockDays));
  const [budget, setBudget] = useState(centsToInputDollars(settings.monthlyPurchasingBudgetCents));
  const [holidayDate, setHolidayDate] = useState('');
  const [holidayName, setHolidayName] = useState('');

  useEffect(() => {
    if (!open) return;
    setWorkingDays(settings.workingDays);
    setHorizon(String(settings.horizonDays));
    setSafetyBuffer(String(settings.safetyBufferDays));
    setReceivingBuffer(String(settings.receivingBufferDays));
    setCoverage(String(settings.orderCoverageDays));
    setSafetyStock(String(settings.safetyStockDays));
    setBudget(centsToInputDollars(settings.monthlyPurchasingBudgetCents));
    setHolidayDate('');
    setHolidayName('');
  }, [open, settings]);

  const toggleDay = (day: number) =>
    setWorkingDays((prev) => (prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].sort()));

  const parseInt_ = (raw: string, label: string, min: number, max: number): number => {
    const n = Number(raw);
    if (!Number.isInteger(n) || n < min || n > max) {
      throw new Error(`${label} must be a whole number between ${min} and ${max}.`);
    }
    return n;
  };

  const handleSave = async () => {
    try {
      if (workingDays.length === 0) throw new Error('Pick at least one working day.');
      const next: PlanningSettings = {
        workingDays,
        horizonDays: parseInt_(horizon, 'Planning horizon', 14, 730),
        safetyBufferDays: parseInt_(safetyBuffer, 'Order-early buffer', 0, 365),
        receivingBufferDays: parseInt_(receivingBuffer, 'Receiving buffer', 0, 90),
        orderCoverageDays: parseInt_(coverage, 'Order coverage', 1, 365),
        safetyStockDays: parseInt_(safetyStock, 'Safety stock', 0, 365),
        monthlyPurchasingBudgetCents: inputDollarsToCents(budget),
      };
      await save.mutateAsync(next);
      toast.success('Planning settings saved');
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save settings.');
    }
  };

  const handleAddHoliday = async () => {
    try {
      await addHoliday.mutateAsync({ date: holidayDate, name: holidayName });
      setHolidayDate('');
      setHolidayName('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not add the holiday.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Planning settings</DialogTitle>
        </DialogHeader>

        <div className="mt-4 grid gap-6 md:grid-cols-2">
          <div className="space-y-5">
            <div className="space-y-1.5">
              <Label>Working days</Label>
              <div className="flex flex-wrap gap-1.5">
                {WEEKDAY_LABELS.map((label, day) => {
                  const on = workingDays.includes(day);
                  return (
                    <button
                      key={label}
                      type="button"
                      disabled={!canEdit}
                      onClick={() => toggleDay(day)}
                      className={cn(
                        'h-9 w-11 rounded-lg border text-xs font-medium transition-colors',
                        on
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-border bg-transparent text-muted-foreground hover:bg-secondary',
                        !canEdit && 'cursor-not-allowed opacity-60',
                      )}
                      aria-pressed={on}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground">Targets are spread over these days; holidays below are skipped.</p>
            </div>

            <NumberField
              id="ps-horizon"
              label="Planning horizon"
              help="How far ahead requirements, orders and the calendar are projected."
              value={horizon}
              onChange={setHorizon}
              disabled={!canEdit}
            />
            <NumberField
              id="ps-coverage"
              label="Order coverage"
              help="Each suggested order covers this many days of usage (before MOQ / pack rounding)."
              value={coverage}
              onChange={setCoverage}
              disabled={!canEdit}
            />
            <NumberField
              id="ps-safety-stock"
              label="Safety stock"
              help="Days of usage to keep on the shelf. Orders are timed so stock never dips below it."
              value={safetyStock}
              onChange={setSafetyStock}
              disabled={!canEdit}
            />
          </div>

          <div className="space-y-5">
            <NumberField
              id="ps-safety-buffer"
              label="Order-early buffer"
              help="Ideal order date = need-by date − worst-case lead time − this. Latest date uses the typical lead time with no buffer."
              value={safetyBuffer}
              onChange={setSafetyBuffer}
              disabled={!canEdit}
            />
            <NumberField
              id="ps-receiving"
              label="Receiving buffer"
              help="Days between a delivery landing on the dock and the parts being usable (inspection, put-away)."
              value={receivingBuffer}
              onChange={setReceivingBuffer}
              disabled={!canEdit}
            />
            <div className="space-y-1.5">
              <Label htmlFor="ps-budget">Monthly purchasing budget</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">$</span>
                <Input
                  id="ps-budget"
                  value={budget}
                  onChange={(e) => setBudget(e.target.value)}
                  placeholder="No cap"
                  inputMode="decimal"
                  className="w-40 tabular-nums"
                  disabled={!canEdit}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Optional. The cash view flags months where suggested orders exceed it.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 space-y-3 border-t border-border pt-5">
          <div className="flex items-baseline justify-between">
            <Label>Holidays &amp; shutdowns</Label>
            <span className="text-xs text-muted-foreground">{holidays.length} on file</span>
          </div>
          {holidays.length > 0 && (
            <ul className="max-h-40 divide-y divide-border overflow-y-auto rounded-md border border-border">
              {holidays.map((h) => (
                <li key={h.id} className="flex items-center justify-between gap-3 px-3 py-1.5 text-sm">
                  <span className="tabular-nums text-muted-foreground">{shortDate(h.holiday_date)}</span>
                  <span className="min-w-0 flex-1 truncate">{h.name}</span>
                  {canEdit && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      aria-label={`Remove ${h.name}`}
                      onClick={() =>
                        deleteHoliday.mutate(h.id, {
                          onError: (err) => toast.error(err instanceof Error ? err.message : 'Could not remove it.'),
                        })
                      }
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
          {canEdit && (
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <DatePicker value={holidayDate} onChange={setHolidayDate} placeholder="Date" className="sm:w-44" />
              <Input
                value={holidayName}
                onChange={(e) => setHolidayName(e.target.value)}
                placeholder="Thanksgiving, summer shutdown…"
                className="flex-1"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void handleAddHoliday();
                  }
                }}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddHoliday}
                disabled={addHoliday.isPending || !holidayDate || !holidayName.trim()}
              >
                <Plus className="h-4 w-4" />
                Add
              </Button>
            </div>
          )}
        </div>

        <DialogFooter className="mt-6">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            {canEdit ? 'Cancel' : 'Close'}
          </Button>
          {canEdit && (
            <Button type="button" onClick={handleSave} disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save settings'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
